import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../ApiController/AuthContext";
import Logo from "../../assets/Logo.svg";
import bgImage from "../../assets/bg2.png";
import badge from "../../assets/badge.svg";
import { getPackage, checkOutPackageById, verifyPackageById, getProfile } from "../../ApiController/ApiService";
import Swal from "sweetalert2";
import { useDashboard } from "../Context/Dashboardcontext";

const SubscriptionPlans = () => {
  const { userId } = useContext(AuthContext);
  const { userProfile, FetchProfile } = useDashboard();
  const [plans, setPlans] = useState([]);
  const [activeTab, setActiveTab] = useState("Regular");
  
  // <-- Nayi states dono packages ki IDs store karne ke liye
  const [currentRegPkgId, setCurrentRegPkgId] = useState(null); 
  const [currentAiPkgId, setCurrentAiPkgId] = useState(null);

  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState("Loading...");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Load all plans
  useEffect(() => {
    const loadPackages = async () => {
      setLoadingText("Loading Pricing Plans...");
      try {
        const packageData = await getPackage();
        
        // Handle if API returns { status, data: [...] } instead of just array
        const packagesArray = Array.isArray(packageData) ? packageData : packageData.data || [];

        const formattedPlans = packagesArray.map((pkg) => ({
          id: pkg._id,
          title: pkg.name ? pkg.name.charAt(0).toUpperCase() + pkg.name.slice(1) : "Unnamed Plan",
          price: pkg.isFree && pkg.price === 0 ? "Free" : `$${pkg.price || 0}`,
          priceSuffix: pkg.isFree && pkg.price === 0 ? "" : "/Month",
          description: pkg.description || "No description available",
          isFree: pkg.isFree || false,
          badge: pkg.name && pkg.name.toLowerCase() === "pro",
          highlight: pkg.name && pkg.name.toLowerCase() === "lite",
          buttonText: "Choose Plan",
          truckLimit: pkg.truck || 1,
          type: pkg.type || "Regular", 
          companyType: pkg.companyType || "Dispax" 
        }));
        setPlans(formattedPlans);
      } catch (error) {
        console.error("Failed to load packages:", error);
        setErrorMessage("Failed to load pricing plans. Please try again.");
        setIsErrorModalOpen(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadPackages();
    FetchProfile();
  }, []);

  // Load user profile to determine current packages
  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;
      try {
        const profileData = await getProfile();
        
        // <-- Dono packages ki IDs yahan nikal kar state mein set kardo
        setCurrentRegPkgId(profileData?.packageId?._id || null);
        setCurrentAiPkgId(profileData?.aiPackageId?._id || null);
        
        setHasUsedFreeTrial(profileData?.isFreePackage === true);
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      }
    };
    fetchProfile();
  }, [userId]);

  // Verify payment if redirected from backend
  useEffect(() => {
    const verifyPayment = async () => {
      const path = location.pathname;
      const packageId = path.split("/").pop();

      if (path.includes("/thankspackage") && packageId && userId && !isSuccessModalOpen) {
        try {
          await verifyPackageById(userId, packageId);
          setSelectedPlan(plans.find((plan) => plan.id === packageId) || null);
          localStorage.setItem("hasSubscription", "true"); 
          setIsSuccessModalOpen(true);
        } catch (error) {
          setErrorMessage(error.message || "Failed to verify payment. Please try again.");
          setIsErrorModalOpen(true);
          navigate(`/failed/${packageId}`, { replace: true });
        }
      } else if (path.includes("/failed") && packageId && !isErrorModalOpen) {
        setErrorMessage("Payment was cancelled or failed. Please try again.");
        setIsErrorModalOpen(true);
      }
    };

    if (!isLoading && plans.length > 0) {
      verifyPayment();
    }
  }, [location, plans, isLoading, userId, navigate, isSuccessModalOpen, isErrorModalOpen]);

  // Handle plan selection
  const handleChoosePlan = async (plan) => {
    const currentUserId = userId || localStorage.getItem("userId");
    if (!currentUserId) {
      setErrorMessage("Please log in to choose a plan.");
      setIsErrorModalOpen(true);
      return;
    }

    if (plan.isFree && hasUsedFreeTrial) {
      Swal.fire({
        title: "Already Used",
        text: "You have already used the free trial and cannot select it again.",
        icon: "info",
        confirmButtonColor: "#04447A",
        confirmButtonText: "OK",
      });
      return;
    }

    // <-- Downgrade Logic Update: Check based on plan type (Ai vs Regular)
    const isAiPlan = plan.type.toLowerCase() === "ai";
    const currentActiveId = isAiPlan ? currentAiPkgId : currentRegPkgId;
    
    const currentPackageObj = plans.find(p => p.id === currentActiveId);
    const currentPrice = currentPackageObj?.price === "Free" ? 0 : Number(currentPackageObj?.price?.replace("$","")) || 0;
    const selectedPrice = plan.isFree ? 0 : Number(plan.price.replace("$","")) || 0;

    if (currentPackageObj && selectedPrice < currentPrice) {
      const result = await Swal.fire({
        title: "Confirm Downgrade",
        text: `You are currently on a higher plan ($${currentPrice}). Are you sure you want to switch to this plan ($${selectedPrice})?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#04447A",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, downgrade",
        cancelButtonText: "Cancel",
      });
      if (!result.isConfirmed) return;
    }

    try {
      setLoadingText(plan.isFree ? "Activating Plan..." : "Processing Payment...");
      setIsLoading(true);
      const response = await checkOutPackageById(currentUserId, plan.id);
      if (response.session && response.session.url) {
        window.location.href = response.session.url;
      } else {
        throw new Error("Payment session could not be created.");
      }
    } catch (error) {
      setErrorMessage(error.message || "Failed to initiate payment. Please try again.");
      setIsErrorModalOpen(true);
      setIsLoading(false);
    }
  };

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setSelectedPlan(null);
    navigate("/companyprofile");
  };

  const closeErrorModal = () => {
    setIsErrorModalOpen(false);
    setErrorMessage("");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white bg-[#080625]">
        {loadingText}
      </div>
    );
  }

  // --- NEW FILTER LOGIC ---
  const filteredPlans = plans.filter((plan) => {
    const compType = plan.companyType?.toLowerCase() || "";
    const isDispaxPackage = compType === "dispax" || compType === "both";
    
    if (!isDispaxPackage) return false;

    const planType = plan.type?.toLowerCase() || "";
    
    if (activeTab === "Regular") {
      return planType === "regular" || planType === "broker"; 
    } else {
      return planType === "ai";
    }
  });

  return (
    <div
      className="min-h-screen w-full overflow-x-hidden py-4 px-4 sm:py-2 sm:px-4 text-white bg-cover bg-center relative"
      style={{
        backgroundImage: `radial-gradient(50% 49.39% at 50% 50%, rgba(29, 36, 97, 0.8) 0%, rgba(8, 6, 37, 0.8) 100%), url(${bgImage})`,
      }}
    >
      <div className="flex justify-center">
        <img src={Logo} alt="DISPAX Logo" className="w-[200px] sm:w-[303px] h-auto" />
      </div>

      <div className="text-center mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-[44px] font-bold px-2 sm:px-0 mt-4">
          Select the Right Plan for Your Company
        </h1>
        <p className="text-gray-300 text-sm sm:text-base mt-2 px-4 sm:px-0">
          Pick a plan that suits your dispatching needs. You can upgrade or downgrade anytime.
        </p>

        {/* --- Tabs UI --- */}
        <div className="flex justify-center gap-4 mt-8">
          <button
            onClick={() => setActiveTab("Regular")}
            className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 ${
              activeTab === "Regular"
                ? "bg-[#0055F9] text-white shadow-[0_0_15px_rgba(0,85,249,0.5)] border border-[#0055F9]"
                : "bg-transparent text-gray-400 border border-gray-600 hover:border-gray-400"
            }`}
          >
            Regular Packages
          </button>
          <button
            onClick={() => setActiveTab("Ai")}
            className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-300 ${
              activeTab === "Ai"
                ? "bg-[#0055F9] text-white shadow-[0_0_15px_rgba(0,85,249,0.5)] border border-[#0055F9]"
                : "bg-transparent text-gray-400 border border-gray-600 hover:border-gray-400"
            }`}
          >
            AI Packages
          </button>
        </div>
      </div>

      {/* Cards Container */}
      <div
        style={{ fontFamily: "Poppins" }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-[1400px] mx-auto px-4 sm:px-0 w-full mt-10 mb-10"
      >
        {filteredPlans.length > 0 ? (
          filteredPlans.map((plan, index) => {
            // <-- Yahan check karein ki kya ye plan ID current active plans mein se ek hai
            const isCurrentPlan = plan.id === currentRegPkgId || plan.id === currentAiPkgId;

            return (
              <div
                key={index}
                className={`relative w-full h-full flex flex-col p-5 sm:p-6 rounded-[20px] border-2 transition-all duration-300 hover:-translate-y-2 ${
                  plan.highlight ? "border-[#DFCFFF] bg-[#0F0D2E]" : "border-gray-700 bg-[#0F0D2E]"
                } ${plan.highlight ? "order-first lg:order-none" : ""}`}
                style={
                  plan.highlight
                    ? {
                        boxShadow:
                          "0 0 30px 8px rgba(178, 109, 255, 0.6), 0 0 60px 16px rgba(178, 109, 255, 0.3)",
                        backdropFilter: "blur(42.7px)",
                      }
                    : { backdropFilter: "blur(42.7px)" }
                }
              >
                {plan.badge && (
                  <div className="absolute top-[-30px] sm:top-[-45px] right-0 sm:right-[-10px] z-10">
                    <img src={badge} alt="badge" className="w-[85px] h-[85px] sm:w-[110px] sm:h-[110px]" />
                  </div>
                )}

                <div className="flex flex-col flex-grow">
                  <h2 className="text-lg sm:text-2xl font-medium leading-tight">
                    {plan.title}
                  </h2>

                  <div className="leading-none mb-3 mt-2 flex items-baseline">
                    <span className="text-3xl sm:text-4xl font-bold text-[#0055F9]">{plan.price}</span>
                    {plan.priceSuffix && (
                      <span className="text-[11px] sm:text-[13px] font-medium text-[#3978FF] ml-1">{plan.priceSuffix}</span>
                    )}
                  </div>

                  <p className="text-xs sm:text-sm font-normal text-gray-300 flex-grow leading-relaxed">
                    {plan.description}
                  </p>

                  <p className="text-sm sm:text-base font-semibold text-white mt-4 border-t border-gray-700 pt-4">
                     Trucks Limit: <span className="text-[#0055F9]">{plan.truckLimit}</span>
                  </p>

                  <div className="mt-6">
                    <button
                      className={`group w-full py-2.5 sm:py-3 rounded-2xl hover:opacity-90 transition font-semibold text-sm sm:text-base
                        ${isCurrentPlan 
                          ? "bg-gray-700 cursor-pointer text-white border border-gray-600 hover:bg-gray-600" 
                          : "bg-white text-black hover:bg-gray-200"
                        }`}
                      onClick={() => {
                        if (isCurrentPlan) {
                          navigate("/dashboard");
                        } else if (plan.isFree || plan.price === "Free" || plan.price === "$0" || plan.price === 0) {
                          Swal.fire({
                            title: "Cannot Purchase",
                            text: "You cannot purchase the free plan. It is automatically assigned to new users.",
                            icon: "warning",
                            confirmButtonColor: "#04447A",
                            confirmButtonText: "OK",
                          });
                        } else {
                          handleChoosePlan(plan);
                        }
                      }}
                    >
                      {isCurrentPlan ? (
                        <>
                          <span className="block group-hover:hidden">Currently Active</span>
                          <span className="hidden group-hover:block">Go to Dashboard</span>
                        </>
                      ) : (
                        plan.buttonText
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-full text-gray-400 text-center w-full py-12 text-xl font-medium">
            No {activeTab} plans available at the moment.
          </div>
        )}
      </div>

      {/* Error Modal */}
      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 overflow-hidden">
          <div className="w-full max-w-md mx-4 overflow-auto">
            <div className="bg-[#0F0D2E] p-6 sm:p-8 rounded-2xl text-center" style={{ backdropFilter: "blur(42.7px)" }}>
              <h2 className="text-2xl sm:text-[30px] font-bold text-white leading-tight">Error</h2>
              <p className="text-xs sm:text-sm text-gray-400 mb-4 sm:mb-6 mt-1 px-2 sm:px-0">{errorMessage}</p>
              <button
                onClick={closeErrorModal}
                className="w-full py-3 bg-[#6F1AFF] text-white rounded-xl hover:opacity-90 transition font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;